import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, designTokens } from '@webapp/ui';

import { supabase } from '../lib/supabase';
import { clearAuthToken } from './authSession';
import { useI18n } from './i18n';

type AccessState = 'checking' | 'anonymous' | 'active' | 'inactive' | 'error';
const ACTIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);

export function ClosedBetaGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [state, setState] = useState<AccessState>('checking');

  const checkAccess = useCallback(async () => {
    setState('checking');
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      setState('error');
      return;
    }
    if (!sessionData.session) {
      setState('anonymous');
      return;
    }

    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('status')
      .maybeSingle();

    if (error) {
      setState('error');
      return;
    }
    setState(data?.status && ACTIVE_STATUSES.has(data.status) ? 'active' : 'inactive');
  }, []);

  useEffect(() => {
    void checkAccess();
    const { data } = supabase.auth.onAuthStateChange(() => void checkAccess());
    return () => data.subscription.unsubscribe();
  }, [checkAccess]);

  if (state === 'anonymous' || state === 'active') return children;

  if (state === 'checking') {
    return <main style={{ display: 'grid', minHeight: '100dvh', placeItems: 'center', padding: 24 }}>
      <div aria-live="polite" style={{ color: designTokens.colors.onSurfaceVariant }}>{t('closedBeta.checking')}</div>
    </main>;
  }

  return <main style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100dvh', padding: 24 }}>
    <section style={{ background: designTokens.colors.surfaceContainerLowest, border: `1px solid ${designTokens.colors.outlineVariant}`, borderRadius: 20, boxShadow: designTokens.shadows.card, maxWidth: 520, padding: '28px 26px', width: '100%' }}>
      <div style={{ color: designTokens.colors.primaryContainer, fontSize: 12, fontWeight: 800, letterSpacing: 1.1, textTransform: 'uppercase' }}>{t('closedBeta.eyebrow')}</div>
      <h1 style={{ fontFamily: 'Fraunces Variable, Georgia, serif', fontSize: 32, lineHeight: 1.12, margin: '8px 0 12px' }}>{t('closedBeta.title')}</h1>
      <p style={{ color: designTokens.colors.onSurfaceVariant, lineHeight: 1.55, margin: 0 }}>{state === 'error' ? t('closedBeta.checkFailed') : t('closedBeta.body')}</p>
      {state === 'inactive' ? <p style={{ color: designTokens.colors.outline, fontSize: 13, lineHeight: 1.5, margin: '14px 0 0' }}>{t('closedBeta.costNote')}</p> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
        <Button onClick={() => void checkAccess()}>{t('closedBeta.checkAgain')}</Button>
        <Button variant="secondary" onClick={() => void clearAuthToken()}>{t('closedBeta.signOut')}</Button>
      </div>
    </section>
  </main>;
}
